import { useEffect, useMemo, useState } from "react";
import { api, formatErr, whatsappShare, downloadCSV, downloadPDF } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, MessageCircle, CheckCircle2, XCircle, Clock, Filter as FilterIcon, ChevronDown, ChevronRight } from "lucide-react";
import { PageHeader, ExportBar } from "@/components/EntityPage";

const LEAD_TYPES = [
  { value: "b2b", label: "B2B" },
  { value: "b2c", label: "B2C" },
];
const STATUSES = [
  { value: "new",              label: "New" },
  { value: "follow-up",        label: "Follow-up" },
  { value: "confirmed",        label: "Confirmed" },
  { value: "advance-received", label: "Advance received" },
  { value: "loss",             label: "Loss" },
];
const CLOSED_STATUSES = ["confirmed", "advance-received", "loss"];

const statusTone = {
  "new":              "bg-sky-500/20 text-sky-300 border-sky-500/40",
  "follow-up":        "bg-amber-500/20 text-amber-300 border-amber-500/40",
  "confirmed":        "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  "advance-received": "bg-emerald-500/25 text-emerald-200 border-emerald-500/50",
  "loss":             "bg-red-500/20 text-red-300 border-red-500/40",
};

function LeadForm({ initial, operators, onSubmit }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState(initial || { lead_type: "b2c", status: "new", entry_date: today });
  const [copyWA, setCopyWA] = useState(!!initial && initial.mobile && initial.mobile === initial.whatsapp);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  function onMobileChange(e) {
    const v = e.target.value;
    setF((s) => ({ ...s, mobile: v, whatsapp: copyWA ? v : s.whatsapp }));
  }
  function toggleCopy(v) {
    setCopyWA(!!v);
    if (v) setF((s) => ({ ...s, whatsapp: s.mobile || s.whatsapp }));
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3 max-h-[75vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Client name</Label>
          <Input required value={f.client_name || ""} onChange={set("client_name")} data-testid="lead-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
          <Input type="email" value={f.email || ""} onChange={set("email")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Mobile</Label>
          <Input value={f.mobile || ""} onChange={onMobileChange} data-testid="lead-mobile" className="bg-[hsl(224_40%_12%)] mt-1"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">WhatsApp</Label>
          <Input value={f.whatsapp || ""} onChange={set("whatsapp")} disabled={copyWA} data-testid="lead-whatsapp" className={`bg-[hsl(224_40%_12%)] mt-1 ${copyWA ? "opacity-70" : ""}`}/>
          <label className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
            <Checkbox checked={copyWA} onCheckedChange={toggleCopy} data-testid="lead-wa-same" className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-black h-3.5 w-3.5"/>
            <span>Same as mobile — auto-copy to WhatsApp</span>
          </label>
        </div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Address</Label>
          <Input value={f.address || ""} onChange={set("address")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Departure</Label>
          <Input value={f.departure || ""} onChange={set("departure")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Destination</Label>
          <Input value={f.destination || ""} onChange={set("destination")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Travel location</Label>
          <Input value={f.travel_location || ""} onChange={set("travel_location")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Entry date</Label>
          <Input type="date" value={f.entry_date || today} onChange={set("entry_date")} data-testid="lead-entry-date" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Start date</Label>
          <Input type="date" value={f.start_date || ""} onChange={set("start_date")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">End date</Label>
          <Input type="date" value={f.end_date || ""} onChange={set("end_date")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Quotation ₹</Label>
          <Input type="number" value={f.quotation || ""} onChange={set("quotation")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Lead type</Label>
          <Select value={f.lead_type || "b2c"} onValueChange={(v) => setF({ ...f, lead_type: v })}>
            <SelectTrigger data-testid="lead-type" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>{LEAD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Follow-up status</Label>
          <Select value={f.status || "new"} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="lead-status" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Assigned to (operator / subadmin / sales)</Label>
          <Select value={f.assigned_to || ""} onValueChange={(v) => setF({ ...f, assigned_to: v })}>
            <SelectTrigger data-testid="lead-assigned" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Assign to operator or sales"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.role})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Follow-up notes</Label>
        <Input value={f.followup_notes || ""} onChange={set("followup_notes")} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
      <div className="flex justify-end">
        <Button type="submit" data-testid="lead-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">{initial ? "Update lead" : "Create lead"}</Button>
      </div>
    </form>
  );
}

function formatElapsed(ms) {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  return { days, hours, mins, secs };
}

export function LeadTimer({ startIso, closedAtIso, status, compact = false }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (CLOSED_STATUSES.includes(status)) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [status]);
  if (!startIso) return <span className="text-muted-foreground text-xs">—</span>;
  const start = new Date(startIso).getTime();
  const isClosed = CLOSED_STATUSES.includes(status);
  const end = isClosed && closedAtIso ? new Date(closedAtIso).getTime() : now;
  const { days, hours, mins, secs } = formatElapsed(end - start);
  if (isClosed) {
    if (status === "loss") return (
      <span className="inline-flex items-center gap-1 text-red-300 text-xs" data-testid="timer-lost">
        <XCircle className="w-3.5 h-3.5"/> Lost after {days}d {hours}h
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 text-emerald-300 text-xs" data-testid="timer-confirmed">
        <CheckCircle2 className="w-3.5 h-3.5"/> Confirmed in {days}d {hours}h {mins}m
      </span>
    );
  }
  if (compact) return (
    <span className="inline-flex items-center gap-1 font-mono text-amber-300 text-xs" data-testid="timer-live">
      <Clock className="w-3.5 h-3.5"/> {days}d {String(hours).padStart(2,"0")}:{String(mins).padStart(2,"0")}:{String(secs).padStart(2,"0")}
    </span>
  );
  return (
    <div className="inline-flex items-center gap-2 text-xs font-mono text-amber-300" data-testid="timer-live">
      <Clock className="w-3.5 h-3.5"/>
      <span>{days}<span className="text-[9px] text-muted-foreground ml-0.5">d</span></span>
      <span>{String(hours).padStart(2,"0")}<span className="text-[9px] text-muted-foreground ml-0.5">h</span></span>
      <span>{String(mins).padStart(2,"0")}<span className="text-[9px] text-muted-foreground ml-0.5">m</span></span>
      <span className="text-slate-400">{String(secs).padStart(2,"0")}<span className="text-[9px] text-muted-foreground ml-0.5">s</span></span>
    </div>
  );
}

const DETAIL_FIELDS = [
  { key: "client_name", label: "Client name" },
  { key: "mobile", label: "Mobile", mono: true },
  { key: "whatsapp", label: "WhatsApp", mono: true, tone: "emerald" },
  { key: "email", label: "Email", mono: true },
  { key: "address", label: "Address" },
  { key: "departure", label: "Departure" },
  { key: "destination", label: "Destination" },
  { key: "travel_location", label: "Travel location" },
  { key: "entry_date", label: "Entry date", mono: true },
  { key: "start_date", label: "Start date", mono: true },
  { key: "end_date", label: "End date", mono: true },
  { key: "lead_type", label: "Lead type", upper: true },
  { key: "status", label: "Status" },
  { key: "assigned_name", label: "Assigned to" },
  { key: "quotation", label: "Quotation", money: true, tone: "sky" },
  { key: "followup_notes", label: "Follow-up notes" },
  { key: "matured_at", label: "Matured at", mono: true },
];

function LeadRow({ r, ops, onEdit, onDelete, onMature, onWhatsApp }) {
  const [open, setOpen] = useState(false);
  const nameOf = (id) => ops.find((o) => o.id === id)?.name || (id ? "—" : "Unassigned");
  const startIso = r.entry_date || r.created_at;
  const enriched = { ...r, assigned_name: nameOf(r.assigned_to), quotation: r.quotation || 0 };

  function cellText(row, c) {
    const v = row[c.key];
    if (v === undefined || v === null || v === "") return "—";
    if (c.money) return `₹${Number(v).toLocaleString("en-IN")}`;
    if (c.upper) return String(v).toUpperCase();
    return String(v);
  }
  function toneCls(c) {
    if (c.tone === "emerald") return "text-emerald-300 font-mono";
    if (c.tone === "sky") return "text-sky-300 font-mono";
    if (c.mono) return "font-mono";
    return "";
  }

  const summary = [
    { label: "Client", val: r.client_name, cls: "" },
    { label: "Contact", val: r.mobile || "—", cls: "font-mono text-xs" },
    { label: "Destination", val: r.destination || "—" },
    { label: "Type", val: (r.lead_type || "b2c").toUpperCase(), cls: "font-mono text-amber-300" },
    { label: "Assigned", val: nameOf(r.assigned_to), cls: "text-xs" },
    { label: "Entry", val: r.entry_date || (r.created_at || "").slice(0, 10) || "—", cls: "font-mono text-xs" },
  ];

  return (
    <div className="rounded-lg border border-border bg-transparent hover:border-amber-500/40 transition-colors" data-testid={`lead-row-${r.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 grid grid-cols-[auto_1fr_auto_auto] items-center gap-4">
        <div className="text-slate-400">{open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-center">
          {summary.map((s) => (
            <div key={s.label} className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{s.label}</div>
              <div className={`text-sm truncate ${s.cls || ""}`}>{s.val}</div>
            </div>
          ))}
        </div>
        <LeadTimer startIso={startIso} closedAtIso={r.matured_at} status={r.status} compact/>
        <Badge className={statusTone[r.status] || statusTone["new"]}>{STATUSES.find((s) => s.value === r.status)?.label || r.status}</Badge>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-border/60" data-testid={`lead-detail-${r.id}`}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 pt-3">
            {DETAIL_FIELDS.map((c) => (
              <div key={c.key} className="grid grid-cols-[130px_1fr] items-center py-1 border-b border-border/30">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                <div className={`text-sm ${toneCls(c)}`}>{cellText(enriched, c)}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5"/> Timer:
              <LeadTimer startIso={startIso} closedAtIso={r.matured_at} status={r.status}/>
            </div>
            <div className="flex gap-2">
              {!CLOSED_STATUSES.includes(r.status) && (
                <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-2" onClick={() => onMature(r)} data-testid={`row-mature-${r.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5"/> Mark advance received
                </Button>
              )}
              <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 gap-2" onClick={() => onWhatsApp(r)}>
                <MessageCircle className="w-3.5 h-3.5"/> WhatsApp
              </Button>
              <Button size="sm" variant="outline" className="border-border bg-[hsl(224_40%_12%)] gap-2" onClick={() => onEdit(r)} data-testid={`row-edit-${r.id}`}>
                <Pencil className="w-3.5 h-3.5"/> Edit
              </Button>
              <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10 gap-2" onClick={() => onDelete(r.id)} data-testid={`row-del-${r.id}`}>
                <Trash2 className="w-3.5 h-3.5"/> Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Leads() {
  const [rows, setRows] = useState([]);
  const [ops, setOps] = useState([]);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fType, setFType] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    const [l, u] = await Promise.all([api.get("/leads"), api.get("/users").catch(() => ({ data: [] }))]);
    setRows(l.data);
    setOps((u.data || []).filter((x) => ["operator", "subadmin", "admin", "sales"].includes(x.role)));
  }
  useEffect(() => { load(); }, []);

  async function save(f) {
    try {
      const payload = { ...f };
      // Clear entry_date if empty; clean assigned_to sentinel
      if (payload.assigned_to === "unassigned") payload.assigned_to = null;
      if (payload.quotation === "") payload.quotation = null;
      else if (payload.quotation != null) payload.quotation = parseFloat(payload.quotation);
      // Close the timer when status transitions to a closed state
      if (CLOSED_STATUSES.includes(payload.status) && !payload.matured_at) {
        payload.matured_at = new Date().toISOString();
      }
      if (!CLOSED_STATUSES.includes(payload.status)) payload.matured_at = null;
      if (editing) await api.put(`/leads/${editing.id}`, payload);
      else await api.post("/leads", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/leads/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  async function markStatus(row, status) {
    try {
      const payload = { ...row, status };
      if (CLOSED_STATUSES.includes(status)) payload.matured_at = new Date().toISOString();
      else payload.matured_at = null;
      await api.put(`/leads/${row.id}`, payload);
      toast.success(`Marked ${status}`); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  const nameOf = (id) => ops.find((o) => o.id === id)?.name || (id ? "—" : "Unassigned");

  const filtered = useMemo(() => rows.filter((r) => {
    if (fStatus !== "all" && r.status !== fStatus) return false;
    if (fType !== "all" && r.lead_type !== fType) return false;
    if (q && !JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, q, fStatus, fType]);

  const exportRows = filtered.map((r) => ({
    client: r.client_name, mobile: r.mobile, whatsapp: r.whatsapp, email: r.email,
    lead_type: r.lead_type, status: r.status, assigned: nameOf(r.assigned_to),
    entry_date: r.entry_date, destination: r.destination, quotation: r.quotation,
    followup_notes: r.followup_notes,
  }));

  return (
    <div>
      <PageHeader title="Leads" subtitle="Live pipeline. Each lead has a running timer from entry until the deal matures."
        right={<>
          <ExportBar rows={exportRows} filename="leads" whatsappText={`Leads: ${filtered.length}`}/>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="add-leads-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New lead</Button></DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-3xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit lead" : "New lead"}</DialogTitle></DialogHeader>
              <LeadForm initial={editing} operators={ops} onSubmit={save}/>
            </DialogContent>
          </Dialog>
        </>}
      />

      <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-4">
        <div className="flex items-center gap-2 mb-4 text-amber-400"><FilterIcon className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">Filters</div></div>
        <div className="grid md:grid-cols-3 gap-4">
          <div><Label className="text-[10px] uppercase text-muted-foreground">Status</Label>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger data-testid="lead-f-status" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[10px] uppercase text-muted-foreground">Type</Label>
            <Select value={fType} onValueChange={setFType}>
              <SelectTrigger data-testid="lead-f-type" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{LEAD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[10px] uppercase text-muted-foreground">Search</Label>
            <div className="flex items-center gap-2 mt-1 bg-[hsl(224_40%_12%)] rounded-md px-2">
              <Search className="w-3.5 h-3.5 text-muted-foreground"/>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client, destination, notes…" className="bg-transparent border-0 h-9 focus-visible:ring-0"/>
            </div></div>
        </div>
      </Card>

      <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex items-center gap-3 mb-4 text-xs text-muted-foreground">
          <span>{filtered.length} of {rows.length}</span>
          <span className="ml-auto">Click any row to see full lead details</span>
        </div>
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No leads match</div>
          ) : filtered.map((r) => (
            <LeadRow key={r.id} r={r} ops={ops}
              onEdit={(x) => { setEditing(x); setOpen(true); }}
              onDelete={del}
              onMature={(x) => markStatus(x, "advance-received")}
              onWhatsApp={(x) => whatsappShare(`Hi ${x.client_name}, following up on your ${x.destination || "tour"} enquiry with Ashok Netravels.`)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}
